import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft, Shield, ShieldOff, KeyRound, Save, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminUsers, adminUpdateProfile, adminResetPassword } from '../../hooks/useAdmin';
import { formatDate } from '../../lib/utils';
import type { Profile } from '../../types/database';

export default function AdminUsersPage() {
  const { users, loading, pagination, fetchUsers } = useAdminUsers();
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [editName, setEditName] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    fetchUsers(search, 0);
  }

  function goToPage(page: number) {
    fetchUsers(search, page);
  }

  function startEdit(user: Profile) {
    setEditingUser(user);
    setEditName(user.display_name ?? '');
  }

  async function saveEdit() {
    if (!editingUser) return;
    const { error } = await adminUpdateProfile(editingUser.id, { display_name: editName });
    if (error) {
      setMessage({ type: 'error', text: error });
    } else {
      setMessage({ type: 'success', text: `Updated ${editingUser.email}` });
      setEditingUser(null);
      fetchUsers(search, pagination.page);
    }
    setTimeout(() => setMessage(null), 3000);
  }

  async function toggleAdmin(user: Profile) {
    const action = user.is_admin ? 'remove admin from' : 'make admin';
    if (!confirm(`Are you sure you want to ${action} ${user.email}?`)) return;

    const { error } = await adminUpdateProfile(user.id, { is_admin: !user.is_admin });
    if (error) {
      setMessage({ type: 'error', text: error });
    } else {
      setMessage({ type: 'success', text: `${user.is_admin ? 'Removed admin from' : 'Granted admin to'} ${user.email}` });
      fetchUsers(search, pagination.page);
    }
    setTimeout(() => setMessage(null), 3000);
  }

  async function resetPassword(user: Profile) {
    if (!confirm(`Send a password reset email to ${user.email}?`)) return;

    const { error } = await adminResetPassword(user.email);
    if (error) {
      setMessage({ type: 'error', text: error });
    } else {
      setMessage({ type: 'success', text: `Password reset email sent to ${user.email}` });
    }
    setTimeout(() => setMessage(null), 3000);
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-gray-400 hover:text-gray-50 transition">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold">Manage Users</h1>
      </div>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-200' : 'bg-red-900/50 border border-red-700 text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            Search
          </button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-400">
              {pagination.totalCount} user{pagination.totalCount !== 1 ? 's' : ''}
              {pagination.totalPages > 1 && ` — page ${pagination.page + 1} of ${pagination.totalPages}`}
            </p>
          </div>
          {users.map((user) => (
            <div key={user.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              {editingUser?.id === user.id ? (
                /* Edit mode */
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Display Name</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <button onClick={saveEdit} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm transition flex items-center gap-1">
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button onClick={() => setEditingUser(null)} className="bg-gray-700 hover:bg-gray-600 text-gray-50 px-3 py-1.5 rounded-lg text-sm transition flex items-center gap-1">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{user.display_name ?? 'No name'}</span>
                      {user.is_admin && (
                        <span className="bg-red-900/50 text-red-300 text-xs px-2 py-0.5 rounded-full flex-shrink-0">admin</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 truncate">{user.email}</p>
                    <p className="text-xs text-gray-600 mt-0.5">Joined {formatDate(user.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => startEdit(user)}
                      className="text-gray-400 hover:text-gray-50 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => resetPassword(user)}
                      className="text-gray-400 hover:text-amber-400 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition"
                      title="Send password reset email"
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleAdmin(user)}
                      className={`px-2 py-1.5 rounded-lg hover:bg-gray-800 transition ${
                        user.is_admin ? 'text-red-400 hover:text-red-300' : 'text-gray-400 hover:text-green-400'
                      }`}
                      title={user.is_admin ? 'Remove admin' : 'Make admin'}
                    >
                      {user.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:bg-gray-800"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i)
                .filter((i) => i === 0 || i === pagination.totalPages - 1 || Math.abs(i - pagination.page) <= 1)
                .reduce<(number | 'ellipsis')[]>((acc, i, idx, arr) => {
                  if (idx > 0 && arr[idx - 1] !== i - 1) acc.push('ellipsis');
                  acc.push(i);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`e${idx}`} className="text-gray-500 px-1">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => goToPage(item)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                        item === pagination.page
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-50'
                      }`}
                    >
                      {item + 1}
                    </button>
                  )
                )}
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:bg-gray-800"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
